import React, { useEffect, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import {
  AppDialog,
  AppDialogContent,
  AppDialogDescription,
  AppDialogHeader,
  AppDialogTitle,
  useToastV2
} from '@easyllama/ui-components'
import { useApp } from '~/context/appContext'
import { fetchSetupIntent } from '~/service/Billing/fetchApiClient'
import AddPaymentMethodForm from '~/molecules/Billing/AddPaymentMethodForm'

const AddPaymentMethodDialog = ({ open, handleOpenChange, onSuccess, onBack }) => {
  const toast = useToastV2()
  const { company, jwt, csrf } = useApp()
  const [stripePromise, setStripePromise] = useState(null)
  const [clientSecret, setClientSecret] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) {
      setStripePromise(null)
      setClientSecret(null)
      return
    }

    let cancelled = false

    const loadSetupContent = async () => {
      setLoading(true)
      try {
        const { data } = await fetchSetupIntent(company.id, jwt, csrf)
        if (cancelled) return
        setStripePromise(loadStripe(data.publishableKey))
        setClientSecret(data.clientSecret)
      } catch (err) {
        if (cancelled) return
        toast.critical(err.message || 'Failed to initialize payment form', undefined, { showCloseButton: false })
        handleOpenChange(false)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadSetupContent()

    return () => { cancelled = true }
  }, [open])

  const handleSuccess = () => {
    handleOpenChange(false)
    setStripePromise(null)
    setClientSecret(null)
    onSuccess?.()
  }

  const handleCancel = () => {
    handleOpenChange(false)
    setStripePromise(null)
    setClientSecret(null)
  }

  return (
    <AppDialog open={open} onOpenChange={handleOpenChange}>
      <AppDialogContent className=' text-llama-grey-7 max-md:!inset-0 max-md:!m-0 max-md:!rounded-none max-md:!max-w-none max-md:!max-h-none max-md:!h-svh max-md:!w-full'>
        <AppDialogHeader
          showBackButton={!!onBack}
          onBack={onBack}
          className='max-md:[&_[data-slot=dialog-back]]:h-[38px]'
        >
          <AppDialogTitle className='max-md:flex max-md:items-center max-md:h-[38px]'>
            Add Payment Method
          </AppDialogTitle>
          <AppDialogDescription className='sr-only'>Add a new payment method</AppDialogDescription>
        </AppDialogHeader>
        {loading && (
          <div className='py-8 text-center text-sm text-llama-grey-5'>Loading payment form...</div>
        )}

        {!loading && stripePromise && clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret, wallets: { link: 'never' } }}>
            <AddPaymentMethodForm onSuccess={handleSuccess} onCancel={handleCancel} />
          </Elements>
        )}
      </AppDialogContent>
    </AppDialog>
  )
}

export default AddPaymentMethodDialog
